/**
 * WorkforceMyScheduleSandbox — the standalone day/week/month schedule route
 * (`workforce.myschedule.sandbox`), kept working after the 2026-07-31 merge.
 *
 * The surface itself now lives in sandbox/schedule-view.tsx and is shared with
 * "My Schedule & Time" (sandbox/WorkforceScheduleTimeSandbox.tsx), which is the
 * screen the rail points at. This file is the thin wrapper for the older link:
 * the "Manage my schedule" menu item and the "This week" button on My Day
 * (copy) still route here. Declares rail="wf-mysched" so the merged rail item
 * is the single highlighted one (rail HARD RULE).
 */
import { useState } from "react";
import { T } from "@v0y4ge/jrni-ui";
import { Screen } from "../../../shell/chrome";
import { DataSourceStrip } from "../../../shell/opSource";
import { useNav } from "../../../shell/router";
import { useWfResource, useBusy } from "../useWorkforce";
import { getMyTime, type MyTimeResponse } from "../wf-api";
import { mondayOf } from "../wf-shared";
import { ScheduleView, fetchView, type Mode, type ViewData } from "./schedule-view";
import { useT } from "../../../lib/i18n";

export default function WorkforceMyScheduleSandbox() {
  const t = useT();
  const nav = useNav();
  const todayIso = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState<Mode>(nav.params.mode === "day" || nav.params.mode === "month" ? (nav.params.mode as Mode) : "week"); // ui-local
  const [anchor, setAnchor] = useState(typeof nav.params.date === "string" ? nav.params.date : todayIso); // ui-local
  const { busy, run } = useBusy();

  const viewKey = mode === "week" ? mondayOf(anchor) : mode === "month" ? anchor.slice(0, 7) : anchor;
  const view = useWfResource<ViewData>(`mysched:${mode}:${viewKey}`, () => fetchView(mode, anchor));
  const myTime = useWfResource<MyTimeResponse>("mysched:mytime", () => getMyTime());
  const reload = () => { view.reload(); myTime.reload(); };

  return (
    <Screen rail="wf-mysched">
      <DataSourceStrip screen="wfmyschedule-sandbox" reqId="REQ-036"
        ops={["getMyDay", "getMySchedule", "getMyTime", "createSwapRequest"]} />
      <div style={{ padding: "18px 24px 28px", fontFamily: T.font, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: T.n1 }}>{t("workforce.mySchedule.title", "My schedule")}</h1>
          <button onClick={() => nav.go("workforce.scheduletime.sandbox", { date: anchor, mode })}
            style={{ marginLeft: "auto", fontSize: 12.5, fontWeight: 600, color: T.p1, background: "none", border: "none", cursor: "pointer", fontFamily: T.font }}>
            {t("workforce.mySchedule.openScheduleTimeOff", "Open My Schedule & Time Off")}
          </button>
        </div>
        <ScheduleView mode={mode} setMode={setMode} anchor={anchor} setAnchor={setAnchor}
          dates={view.data?.dates || []} days={view.data?.days || []} roster={view.data?.roster || {}} sched={view.data?.sched || null} myTime={myTime.data}
          loading={view.loading && !view.data} error={view.error}
          busy={busy} run={run} todayIso={todayIso} onReload={reload}
          onRequestTimeOff={() => nav.go("workforce.scheduletime.sandbox", { tab: "timeoff" })}
          onReportSick={(iso: string) => nav.go("workforce.myday.sandbox", { date: iso, sick: "1" })} />
      </div>
    </Screen>
  );
}
